import React, { useState } from 'react';
import siteSetting from '../../config/env';
import { decisionFor, resolveText } from './decisions';
import { IconTick, IconMail, IconPencil, IconBack, initials } from './icons';

/**
 * Cards for the "Needs you" queue (plan §4.3). Each receives the queue item
 * and an `onAnswer(answer)` that the queue forwards to the job, then advances.
 */

const REASON_LABEL = { phone: 'Same mobile number', email: 'Same email', name_dob: 'Same name and date of birth' };

function Person({ p, warm }) {
  const meta = [p.phone || p.email, p.visits != null ? `${p.visits} visit${p.visits === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');
  return (
    <div className="ob-person">
      <div className={`ob-person-av ${warm ? 'ob-person-av--warm' : ''}`}>{initials(p.name)}</div>
      <div>
        <b>{p.name}</b>
        <div className="ob-person-meta">{meta}</div>
        {p.sourceFile && <div className="ob-person-src">{p.sourceFile}{p.sourceRow ? ` · row ${p.sourceRow}` : ''}</div>}
      </div>
    </div>
  );
}

export function DuplicatePairCard({ item, onKeepBoth, onSamePerson, exiting }) {
  return (
    <>
      <div className={`ob-card ${exiting ? 'ob-card--out' : ''}`}>
        <Person p={item.a} />
        <div className="ob-reason"><span>{REASON_LABEL[item.reason] || 'Looks like a match'}</span></div>
        <Person p={item.b} warm />
      </div>
      <div className="ob-answers">
        <button type="button" className="ob-btn ob-btn--secondary" onClick={onKeepBoth}>Keep both</button>
        <button type="button" className="ob-btn ob-btn--primary" onClick={onSamePerson}>Same person</button>
      </div>
    </>
  );
}

/**
 * Mock stand-in for clients/MergeClientsView. The real view calls
 * /user-merge/preview with real ids, which the mock job does not have; this
 * mirrors its layout (survivor cards, field picks, history summary, confirm)
 * so the flow can be reviewed end to end. Swapped for the real component
 * when the API is on (see NeedsYouQueue).
 */
export function MockMergeView({ pair, onBack, onConfirmed }) {
  const [survivor, setSurvivor] = useState('a');
  const [namePick, setNamePick] = useState('a');
  const [busy, setBusy] = useState(false);
  const A = pair.a; const B = pair.b;
  const total = (A.visits || 0) + (B.visits || 0);
  const confirm = async () => { setBusy(true); await onConfirmed({ survivor: survivor === 'a' ? A : B, namePick }); };
  return (
    <div className="ob-screen">
      <div className="ob-eyebrow-row">
        <button type="button" className="ob-link-btn" onClick={onBack} style={{ padding: 0, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconBack /> Back</button>
        <span className="ob-eyebrow">Merge clients</span>
      </div>
      <div className="ob-screen-scroll">
        <p className="ob-sub" style={{ margin: 0 }}>Merging combines two client records into one. Choose which record to keep, then which details to carry over. You can undo this within 72 hours.</p>
        <div className="ob-eyebrow">Which record to keep</div>
        <div className="ob-answers" style={{ marginTop: 0 }}>
          {[['a', A], ['b', B]].map(([k, p]) => (
            <button type="button" key={k} className={`ob-choice ${survivor === k ? 'ob-choice--selected' : ''}`} onClick={() => setSurvivor(k)}>
              <b>{p.name}</b>
              <span>{p.email || 'No email'}</span>
              <span>{p.visits || 0} bookings{survivor === k ? ' · Kept as the main record' : ''}</span>
            </button>
          ))}
        </div>
        <p className="ob-footnote" style={{ textAlign: 'left', maxWidth: 'none' }}>The kept record holds the login and account. Tap the other card to switch.</p>
        <div className="ob-eyebrow">Details to keep</div>
        <div>
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Name</div>
          <div className="ob-answers" style={{ marginTop: 0 }}>
            {[['a', A], ['b', B]].map(([k, p]) => (
              <button type="button" key={k} className={`ob-choice ${namePick === k ? 'ob-choice--selected' : ''}`} onClick={() => setNamePick(k)}><b>{p.name}</b></button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Phone</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{A.phone || B.phone || 'None'}</div>
        </div>
        <div className="ob-card" style={{ boxShadow: 'none', background: '#FAFAFB' }}>
          <b style={{ fontSize: 13 }}>Booking history &amp; rewards</b>
          <p className="ob-sub" style={{ margin: '6px 0 0' }}>All {total} bookings from both records ({A.visits || 0} + {B.visits || 0}) move onto the kept record, along with any loyalty points, stamps and rewards. Nothing is lost.</p>
        </div>
      </div>
      <div className="ob-answers">
        <button type="button" className="ob-btn ob-btn--secondary" onClick={onBack} disabled={busy}>Cancel</button>
        <button type="button" className="ob-btn ob-btn--primary" onClick={confirm} disabled={busy}>{busy ? 'Merging…' : 'Confirm merge'}</button>
      </div>
    </div>
  );
}

export function UnmappedServiceCard({ item, onAnswer, exiting }) {
  const s = item.service || {};
  const [newName, setNewName] = useState('');
  const price = s.price != null ? `£${Number(s.price).toFixed(2)}` : null;
  const dur = s.duration ? `${s.duration} min` : null;
  return (
    <>
      <div className={`ob-card ${exiting ? 'ob-card--out' : ''}`}>
        <b style={{ fontSize: 15 }}>{s.name}</b>
        <div className="ob-person-meta">{[price, dur].filter(Boolean).join(' · ')}</div>
        <div className="ob-sub" style={{ margin: '10px 0 0' }}>Which category does this belong in?</div>
        <div className="ob-answers ob-answers--stack" style={{ marginTop: 10 }}>
          {(item.candidates || []).map((c) => (
            <button type="button" key={c.categoryId} className="ob-choice" onClick={() => onAnswer({ categoryId: c.categoryId })}><b>{c.name}</b></button>
          ))}
          <div className="ob-staff-email">
            <IconPencil />
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Or type a new category" aria-label="New category name" />
            <button type="button" className="ob-staff-toggle" disabled={!newName.trim()} onClick={() => onAnswer({ newCategoryName: newName.trim() })}>Add</button>
          </div>
        </div>
      </div>
      <div className="ob-answers ob-answers--1">
        <button type="button" className="ob-btn ob-btn--ghost" onClick={() => onAnswer({ action: 'skip' })}>Skip, put it in Uncategorised</button>
      </div>
    </>
  );
}

export function UnresolvedBookingCard({ item, onAnswer, exiting }) {
  const b = item.booking || {};
  const what = item.missing === 'staff' ? 'who this was with' : 'which service this was';
  const key = item.missing === 'staff' ? 'hairdresserId' : 'serviceId';
  let when = '';
  try { when = new Date(b.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }); } catch (e) { when = b.date; }
  return (
    <>
      <div className={`ob-card ${exiting ? 'ob-card--out' : ''}`}>
        <b style={{ fontSize: 15 }}>{b.clientName}</b>
        <div className="ob-person-meta">{when} · {b.start}{b.end ? `–${b.end}` : ''}</div>
        <div className="ob-sub" style={{ margin: '10px 0 0' }}>We couldn't tell {what}.</div>
        <div className="ob-answers ob-answers--stack" style={{ marginTop: 10 }}>
          {(item.candidates || []).map((c) => (
            <button type="button" key={c.id} className="ob-choice" onClick={() => onAnswer({ [key]: c.id })}><b>{c.name}</b></button>
          ))}
        </div>
      </div>
      <div className="ob-answers ob-answers--1">
        <button type="button" className="ob-btn ob-btn--ghost" onClick={() => onAnswer({ action: 'skip' })}>
          <span className="ob-btn--stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>Skip this booking<small>It will be listed in your import report.</small></span>
        </button>
      </div>
    </>
  );
}

function HoursEditor({ hours, onChange }) {
  const update = (i, patch) => onChange(hours.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  return (
    <div className="ob-hours">
      {hours.map((h, i) => (
        <div className="ob-hour" key={h.dayName}>
          <b style={{ fontWeight: 600 }}>{h.dayName}</b>
          {h.isClose ? (
            <span className="ob-hour-closed">Closed</span>
          ) : (
            <span className="ob-hour-times">
              <input type="time" value={h.sTime || '09:00'} onChange={(e) => update(i, { sTime: e.target.value })} aria-label={`${h.dayName} opens`} />
              <span>to</span>
              <input type="time" value={h.eTime || '17:00'} onChange={(e) => update(i, { eTime: e.target.value })} aria-label={`${h.dayName} closes`} />
            </span>
          )}
          <button type="button" className="ob-hour-toggle" onClick={() => update(i, h.isClose ? { isClose: false, sTime: h.sTime || '09:00', eTime: h.eTime || '17:00' } : { isClose: true })}>
            {h.isClose ? 'Open this day' : 'Close this day'}
          </button>
        </div>
      ))}
    </div>
  );
}

export function DecisionCard({ item, onAnswer, exiting }) {
  const def = decisionFor(item.key);
  const [hours, setHours] = useState(item.context?.hours || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!def) return null;
  const title = resolveText(def.title, item.context);
  const sub = resolveText(def.sub, item.context);

  const submit = async (value) => {
    setBusy(true); setErr('');
    try {
      await def.write(value, item.context);
      await onAnswer({ value });
    } catch (e) {
      setErr(e?.response?.data?.messages || e.message || 'Could not save that. Try again.');
      setBusy(false);
    }
  };

  return (
    <>
      <div className={`ob-card ${exiting ? 'ob-card--out' : ''}`}>
        <b style={{ fontSize: 16, display: 'block', textWrap: 'balance' }}>{title}</b>
        {sub && <div className="ob-sub" style={{ margin: '6px 0 0' }}>{sub}</div>}
        {def.custom === 'hours' ? (
          <div style={{ marginTop: 12 }}><HoursEditor hours={hours} onChange={setHours} /></div>
        ) : (
          <div className={`ob-answers ${def.choices.length === 1 ? 'ob-answers--1' : ''}`}>
            {def.choices.map((c) => (
              <button type="button" key={String(c.value)} className="ob-choice" disabled={busy} onClick={() => submit(c.value)}>
                <b>{c.label}</b>
                {c.hint && <span>{c.hint}</span>}
              </button>
            ))}
          </div>
        )}
        {err && <div className="ob-error" role="alert" style={{ marginTop: 10 }}>{err}</div>}
      </div>
      {def.custom === 'hours' && (
        <div className="ob-answers">
          <button type="button" className="ob-btn ob-btn--secondary" disabled={busy} onClick={() => onAnswer({ value: 'skip' })}>Decide later</button>
          <button type="button" className="ob-btn ob-btn--primary" disabled={busy} onClick={() => submit(hours)}>{busy ? 'Saving…' : 'Looks right'}</button>
        </div>
      )}
    </>
  );
}

/**
 * Staff review, with AddStaff's rules: Employed = Included, Self-employed =
 * +£{oneSelfEmpPrice}/mo (siteSetting.stripe.oneSelfEmpPrice), plan line,
 * cost stated ON the CTA, "Invite later" keeps rotas with no charge.
 */
export function StaffReviewCard({ item, currentSelfEmployed = 0, planInterval = 'month', onInvite, onLater, exiting }) {
  const price = siteSetting?.stripe?.oneSelfEmpPrice || 5;
  const [rows, setRows] = useState((item.staff || []).map((s) => ({ ...s, email: s.email || '', editing: !s.email })));
  const [busy, setBusy] = useState(false);
  const selfEmp = rows.filter((r) => r.employmentTypeId === 2).length;
  const invitable = rows.filter((r) => /\S+@\S+\.\S+/.test(r.email));
  const addCost = selfEmp * price;
  const per = planInterval === 'year' ? 'yr' : 'mo';
  const setEmail = (tempId, email) => setRows((rs) => rs.map((r) => (r.tempId === tempId ? { ...r, email } : r)));
  const toggleEdit = (tempId) => setRows((rs) => rs.map((r) => (r.tempId === tempId ? { ...r, editing: !r.editing } : r)));
  const setType = (tempId, employmentTypeId) => setRows((rs) => rs.map((r) => (r.tempId === tempId ? { ...r, employmentTypeId } : r)));

  const invite = async () => {
    setBusy(true);
    await onInvite(invitable.map(({ tempId, email, employmentTypeId, name }) => ({ tempId, email, employmentTypeId, name })), rows.filter((r) => !invitable.includes(r)).map((r) => r.tempId));
  };

  return (
    <>
      <div className="ob-sub">Found in your files, with their hours where the files had them. Check each one: the invite goes to this email, and self-employed seats change your plan.</div>
      <div className={`ob-staff ${exiting ? 'ob-card--out' : ''}`}>
        {rows.map((r) => (
          <div className="ob-staff-card" key={r.tempId}>
            <div className="ob-staff-top">
              <div className="ob-staff-av">{initials(r.name)}</div>
              <div className="ob-staff-name">
                <b>{r.name}</b>
                <span>{r.daysPattern}{r.bookingsFound ? ` · ${r.bookingsFound.toLocaleString('en-GB')} bookings found` : ''}</span>
              </div>
              <button
                type="button"
                className={`ob-badge ${r.employmentTypeId === 2 ? 'ob-badge--price' : ''}`}
                onClick={() => setType(r.tempId, r.employmentTypeId === 2 ? 1 : 2)}
                title="Tap to change"
              >
                {r.employmentTypeId === 2 ? `Self-employed · +£${price}/${per}` : 'Employed · Included'}
              </button>
            </div>
            <div className="ob-staff-email">
              <IconMail />
              {r.editing ? (
                <input type="email" value={r.email} placeholder="Email for the invite" onChange={(e) => setEmail(r.tempId, e.target.value)} onBlur={() => { if (/\S+@\S+\.\S+/.test(r.email)) toggleEdit(r.tempId); }} autoFocus={!r.email} aria-label={`Invite email for ${r.name}`} />
              ) : (
                <span style={{ flex: 1, fontSize: 13 }}>{r.email}</span>
              )}
              <button type="button" className="ob-staff-toggle" onClick={() => toggleEdit(r.tempId)}>{r.editing ? 'Done' : 'Edit'}</button>
            </div>
          </div>
        ))}
      </div>
      {selfEmp > 0 && (
        <div className="ob-plan">
          <span>New plan</span>
          <b>Salon + {currentSelfEmployed + selfEmp} self-employed · +£{addCost}/{per}</b>
        </div>
      )}
      <div className="ob-answers ob-answers--stack">
        <button type="button" className="ob-btn ob-btn--primary" disabled={busy || !invitable.length} onClick={invite}>
          {busy ? 'Sending…' : `Send ${invitable.length} invite${invitable.length === 1 ? '' : 's'}${addCost ? ` · adds £${addCost}/${per}` : ''}`}
        </button>
        <button type="button" className="ob-btn ob-btn--ghost ob-btn--stack" disabled={busy} onClick={() => onLater(rows.map((r) => r.tempId))}>
          <span>Invite later</span>
          <small>Their rotas stay built. No charge until an invite is sent.</small>
        </button>
      </div>
    </>
  );
}

export const TickSmall = () => <IconTick size={12} />;
